import React, { useState, useEffect } from 'react';
import { AuthData } from '../provider/AuthProvider';
import { FaIdCard, FaCopy, FaCheck } from 'react-icons/fa';

const InternIDDisplay = () => {
  const { user, getUserInternID } = AuthData();
  const [internID, setInternID] = useState('');
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchInternID = async () => {
      if (user?.userd?.uid) {
        try {
          // First check if internID is in user profile
          if (user.profile?.internID) {
            setInternID(user.profile.internID);
          } else {
            // Fallback to fetching from database
            const id = await getUserInternID(user.userd.uid);
            setInternID(id || 'Not assigned');
          }
        } catch (error) {
          console.error('Error fetching intern ID:', error);
          setInternID('Error loading ID');
        } finally {
          setLoading(false);
        }
      }
    };

    fetchInternID();
  }, [user, getUserInternID]);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(internID);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center space-x-2 text-gray-500">
        <FaIdCard />
        <span>Loading ID...</span>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-r from-orange-50 to-orange-100 border border-orange-200 rounded-lg p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="bg-orange-500 p-2 rounded-full">
            <FaIdCard className="text-white" />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-600">Your Intern ID</p>
            <p className="text-lg font-bold text-orange-600 font-mono tracking-wider">
              {internID}
            </p>
          </div>
        </div>
        
        {internID && internID !== 'Not assigned' && internID !== 'Error loading ID' && (
          <button
            onClick={copyToClipboard}
            className="flex items-center space-x-1 px-3 py-1 bg-white border border-orange-300 rounded-md hover:bg-orange-50 transition-colors"
            title="Copy to clipboard"
          >
            {copied ? (
              <>
                <FaCheck className="text-green-500 text-sm" />
                <span className="text-green-500 text-sm">Copied!</span>
              </>
            ) : (
              <>
                <FaCopy className="text-orange-600 text-sm" />
                <span className="text-orange-600 text-sm">Copy</span>
              </>
            )}
          </button>
        )}
      </div>
      
      {internID && internID.startsWith('IN') && (
        <div className="mt-3 text-xs text-gray-500">
          <p>• Keep this ID safe - you'll need it for applications</p>
          <p>• Format: IN + 5 digits + first letter of your name</p>
        </div>
      )}
    </div>
  );
};

// Component to show intern ID in profile header
export const ProfileInternID = () => {
  const { user } = AuthData();
  const internID = user?.profile?.internID;

  if (!internID) return null;

  return (
    <div className="flex items-center space-x-2 text-orange-600">
      <FaIdCard className="text-sm" />
      <span className="font-mono text-sm font-medium">{internID}</span>
    </div>
  );
};

// Mini component for cards/lists
export const InternIDBadge = ({ internID, size = 'sm' }) => {
  if (!internID) return null;

  const sizeClasses = {
    xs: 'text-xs px-2 py-1',
    sm: 'text-sm px-2 py-1',
    md: 'text-base px-3 py-1',
    lg: 'text-lg px-4 py-2'
  };

  return (
    <span className={`inline-flex items-center bg-orange-100 text-orange-800 font-mono font-medium rounded-full ${sizeClasses[size]}`}>
      <FaIdCard className="mr-1" />
      {internID}
    </span>
  );
};

export default InternIDDisplay;